import { useEffect, useState } from 'react';
import axios from 'axios';
import { KeyRound, Phone, Check, X, RefreshCw } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Live "Forgot Password" inbox button for the admin header.
 *
 * Polls every 30s. When players tap "Forgot password?" on the login screen,
 * the count goes up here. Click → modal with a list. Each row has:
 *   - Open in Users tab (so admin can reset password the normal way)
 *   - Mark resolved (closes the ticket)
 *   - Dismiss (delete the request, e.g. typo / spam)
 *
 * Renders nothing until at least one request exists, so it stays out of the
 * way most of the time.
 */
const ForgotPasswordInbox = ({ onOpenUser }) => {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    // When we issue a temp password, stash it here keyed by request id so
    // we can show the plaintext ONCE with a copy button. Cleared on close.
    const [tempPasswords, setTempPasswords] = useState({});
    const [issuing, setIssuing] = useState(null);
    // Manual password set (cashier/operator/admin): keyed by request id.
    const [manualFor, setManualFor] = useState(null);   // request id currently being set manually
    const [manualVal, setManualVal] = useState('');
    const [savingManual, setSavingManual] = useState(null);
    const [manualSetIds, setManualSetIds] = useState([]); // ids whose value was manually set (label "Set")

    const fetchCount = async () => {
        try {
            // /staff/ endpoint works for both admin and cashier (scoped).
            const r = await axios.get(`${API}/staff/password-reset-requests/count`);
            setCount(r.data?.pending || 0);
        } catch {
            // Ignore — endpoint may be unreachable briefly
        }
    };

    const fetchList = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/staff/password-reset-requests?status=pending`);
            setRows(r.data?.rows || []);
            setCount(r.data?.pending_count || 0);
        } catch {
            toast.error('Could not load reset requests');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchCount();
        const id = setInterval(fetchCount, 30_000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (open) fetchList();
        else setTempPasswords({});  // clear stashed temps when closing
    }, [open]);

    const sendTemp = async (id) => {
        setIssuing(id);
        try {
            const r = await axios.post(
                `${API}/staff/password-reset-requests/${id}/send-temp`, {},
            );
            const temp = r.data?.temp_password;
            if (temp) {
                setTempPasswords((s) => ({ ...s, [id]: temp }));
                // Auto-copy immediately so the value is safe on the clipboard
                // even if the admin doesn't tap Copy in time.
                let copied = false;
                try { await navigator.clipboard.writeText(temp); copied = true; } catch { /* clipboard blocked */ }
                toast.success(copied
                    ? `Temp password ${temp} copied — send it to the player`
                    : `Temp password: ${temp} — copy & send to player`,
                    { duration: 15000 });
                // NOTE: issuing a temp marks the request "resolved" on the
                // backend. Do NOT refetch the pending list here — that would
                // drop this row (and hide the temp) before the admin can copy
                // it. Keep the row pinned with its temp until the dialog closes;
                // just decrement the badge locally.
                setCount((c) => Math.max(0, c - 1));
            } else {
                fetchList();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not issue temp');
        } finally { setIssuing(null); }
    };

    const copyTemp = async (id) => {
        const val = tempPasswords[id];
        if (!val) return;
        try {
            await navigator.clipboard.writeText(val);
            toast.success('Copied to clipboard');
        } catch {
            toast.message(`Password: ${val}`);
        }
    };

    // Manually set a chosen password for the player (scoped on the backend).
    const saveManual = async (r) => {
        const val = (manualVal || '').trim();
        if (val.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        setSavingManual(r.id);
        try {
            await axios.post(`${API}/staff/set-player-password`, {
                user_id: r.user_id, new_password: val, req_id: r.id,
            });
            // Pin the value in the row (persistent) + auto-copy as a safeguard.
            setTempPasswords((s) => ({ ...s, [r.id]: val }));
            setManualSetIds((s) => Array.from(new Set([...s, r.id])));
            let copied = false;
            try { await navigator.clipboard.writeText(val); copied = true; } catch { /* clipboard blocked */ }
            toast.success(copied
                ? `Password set & copied — send it to the player`
                : `Password set: ${val} — copy & send to player`,
                { duration: 15000 });
            setManualFor(null); setManualVal('');
            // Do NOT refetch (would drop this now-resolved row before they copy).
            setCount((c) => Math.max(0, c - 1));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not set password');
        } finally { setSavingManual(null); }
    };

    const resolve = async (id) => {
        try {
            await axios.post(`${API}/admin/password-reset-requests/${id}/resolve`, {});
            toast.success('Marked resolved');
            fetchList();
        } catch {
            toast.error('Could not resolve');
        }
    };

    const dismiss = async (id) => {
        try {
            await axios.delete(`${API}/admin/password-reset-requests/${id}`);
            toast.message('Dismissed');
            fetchList();
        } catch {
            toast.error('Could not dismiss');
        }
    };

    const fmtWhen = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleString('en-US', {
                timeZone: 'America/Chicago',
                month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit',
            }) + ' CT';
        } catch { return ''; }
    };

    // Hide entirely when there's nothing pending — keeps the header clean
    if (count === 0) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors"
                title={`${count} pending password reset request${count === 1 ? '' : 's'}`}
                data-testid="forgot-pw-inbox-btn"
            >
                <KeyRound className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">{count}</span>
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFD700]" />
                            Password Reset Requests
                            <button
                                type="button"
                                onClick={fetchList}
                                className="ml-auto text-[#A0AAB2] hover:text-white"
                                title="Refresh"
                                data-testid="forgot-pw-refresh"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Players who tapped &quot;Forgot password?&quot; on the login screen.
                            Tap Send Temp to issue a 6-digit code — player must change it on next login.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[55vh] overflow-y-auto -mx-2 px-2">
                        {rows.length === 0 ? (
                            <p className="text-center text-[#A0AAB2] py-8">No pending requests.</p>
                        ) : (
                            <ul className="space-y-2">
                                {rows.map((r) => (
                                    <li
                                        key={r.id}
                                        className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3"
                                        data-testid={`forgot-pw-row-${r.id}`}
                                    >
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-white font-bold break-words">{r.name || 'Unnamed'}</span>
                                                    <span className="inline-flex items-center gap-1 text-xs text-[#A0AAB2]">
                                                        <Phone className="w-3 h-3" />
                                                        {r.phone || '—'}
                                                    </span>
                                                    {r.retry_count > 0 && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF6B00]/20 text-[#FF6B00] font-bold">
                                                            +{r.retry_count} more taps
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-[#A0AAB2] mt-1">
                                                    {fmtWhen(r.updated_at || r.created_at)}
                                                </p>
                                                {tempPasswords[r.id] && (
                                                    <div className="mt-2 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/40 p-2 flex items-center gap-2"
                                                        data-testid={`forgot-pw-temp-${r.id}`}>
                                                        <span className="text-[10px] uppercase tracking-widest text-[#39FF14] font-black">
                                                            {manualSetIds.includes(r.id) ? 'Set' : 'Temp'}
                                                        </span>
                                                        <code className="text-lg font-mono font-black text-[#39FF14] tracking-widest">
                                                            {tempPasswords[r.id]}
                                                        </code>
                                                        <button
                                                            type="button"
                                                            onClick={() => copyTemp(r.id)}
                                                            className="ml-auto text-[10px] px-2 py-1 rounded bg-[#39FF14] text-black font-black hover:brightness-110"
                                                            data-testid={`forgot-pw-copy-${r.id}`}
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                )}
                                                {manualFor === r.id && !tempPasswords[r.id] && (
                                                    <div className="mt-2 flex items-center gap-2" data-testid={`forgot-pw-manual-row-${r.id}`}>
                                                        <Input
                                                            autoFocus
                                                            value={manualVal}
                                                            onChange={(e) => setManualVal(e.target.value)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') saveManual(r); }}
                                                            placeholder="New password (min 6 chars)"
                                                            className="h-8 text-xs bg-[#0d0d0d] border-[#FFD700]/40 text-white"
                                                            data-testid={`forgot-pw-manual-input-${r.id}`}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            onClick={() => saveManual(r)}
                                                            disabled={savingManual === r.id}
                                                            className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold h-8 text-xs disabled:opacity-50"
                                                            data-testid={`forgot-pw-manual-save-${r.id}`}
                                                        >
                                                            Save
                                                        </Button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setManualFor(null); setManualVal(''); }}
                                                            className="text-[#A0AAB2] hover:text-[#FF3B30] p-1"
                                                            title="Cancel"
                                                            data-testid={`forgot-pw-manual-cancel-${r.id}`}
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap sm:shrink-0 sm:flex-nowrap justify-end">
                                                {!tempPasswords[r.id] && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => sendTemp(r.id)}
                                                        disabled={issuing === r.id}
                                                        className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold h-8 text-xs disabled:opacity-50"
                                                        data-testid={`forgot-pw-sendtemp-${r.id}`}
                                                    >
                                                        <KeyRound className="w-3 h-3 mr-1" />
                                                        Send Temp
                                                    </Button>
                                                )}
                                                {!tempPasswords[r.id] && manualFor !== r.id && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => { setManualFor(r.id); setManualVal(''); }}
                                                        className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 h-8 text-xs"
                                                        data-testid={`forgot-pw-setmanual-${r.id}`}
                                                    >
                                                        Set manually
                                                    </Button>
                                                )}
                                                {typeof onOpenUser === 'function' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            if (r.user_id) {
                                                                onOpenUser(r.user_id);
                                                                setOpen(false);
                                                            }
                                                        }}
                                                        className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 h-8 text-xs"
                                                        data-testid={`forgot-pw-open-${r.id}`}
                                                    >
                                                        Open
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => resolve(r.id)}
                                                    className="border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 h-8 text-xs"
                                                    data-testid={`forgot-pw-resolve-${r.id}`}
                                                >
                                                    <Check className="w-3 h-3 mr-1" />Done
                                                </Button>
                                                <button
                                                    type="button"
                                                    onClick={() => dismiss(r.id)}
                                                    className="text-[#A0AAB2] hover:text-[#FF3B30] p-1"
                                                    title="Dismiss (delete)"
                                                    data-testid={`forgot-pw-dismiss-${r.id}`}
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <DialogFooter>
                        <p className="text-[10px] text-[#A0AAB2]/60 mr-auto">
                            Tip — admin reset no longer forces a 2nd password change, so the value you set sticks.
                        </p>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ForgotPasswordInbox;
